// Downloading a stored file drop (docs/adr/0015, crypto-spec §7): records
// are read by Range a batch at a time, each opened by @sss/crypto, and
// their bytes written into the files in manifest order. Nothing is written
// until its record has been checked; a drop that stops part way has not
// produced a file. A dropped connection retries the same batch.

import { DROP_RECORD_BYTES, type DropManifest, type DropSession } from '@sss/crypto';
import { DROP_LIMITS } from '@sss/types';
import { isRetryable } from './dropsApi';
import { retryDelay } from './dropUpload';
import type { FileSink, SavedFile } from './fileSink';

/** Records per Range read: as many as the Worker serves at once. */
export const RECORDS_PER_READ = Math.floor(DROP_LIMITS.maxRangeBytes / DROP_RECORD_BYTES);

/** The byte range (inclusive) holding records [first, first + count). */
export const recordRange = (first: number, count: number): { start: number; end: number } => ({
  start: first * DROP_RECORD_BYTES,
  end: (first + count) * DROP_RECORD_BYTES - 1,
});

/**
 * Pours the drop's joined data into its files in order: each file is begun
 * when its first byte arrives (or at once, if it's empty) and ended when its
 * last byte is written.
 */
export class FileSplitter {
  private index = -1;
  private left = 0;
  private readonly saved: SavedFile[] = [];

  constructor(
    private readonly manifest: DropManifest,
    private readonly sink: FileSink,
  ) {}

  /** Move on to the next file, ending finished ones (and passing straight through empty ones). */
  private async next(): Promise<boolean> {
    while (this.left === 0) {
      if (this.index >= 0) this.saved.push(await this.sink.end());
      this.index += 1;
      const file = this.manifest.files[this.index];
      if (!file) return false;
      await this.sink.begin(file);
      this.left = file.size;
    }
    return true;
  }

  async write(data: Uint8Array): Promise<void> {
    let offset = 0;
    while (offset < data.length) {
      if (!(await this.next())) throw new Error('More data than the manifest lists');
      const take = Math.min(this.left, data.length - offset);
      await this.sink.write(data.subarray(offset, offset + take));
      this.left -= take;
      offset += take;
    }
  }

  /** End the last file, and any empty files after it. */
  async finish(): Promise<SavedFile[]> {
    await this.next();
    return this.saved;
  }
}

const wait = (seconds: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, seconds * 1000);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(signal.reason);
    });
  });

export interface DownloadOptions {
  manifest: DropManifest;
  session: DropSession;
  sink: FileSink;
  /** Read bytes [start, end] of the stored data. */
  read(start: number, end: number, signal: AbortSignal): Promise<Uint8Array>;
  onRecords(done: number): void;
  onRetry?(seconds: number | null): void;
  signal: AbortSignal;
}

/** Read, open and write every record, then end the files. Rejects on tampering, a hard error, or abort. */
export const downloadRecords = async (options: DownloadOptions): Promise<SavedFile[]> => {
  const { session } = options;
  const splitter = new FileSplitter(options.manifest, options.sink);
  try {
    for (let first = 0; first < session.totalRecords; first += RECORDS_PER_READ) {
      const count = Math.min(RECORDS_PER_READ, session.totalRecords - first);
      const { start, end } = recordRange(first, count);
      let bytes: Uint8Array;
      for (let attempt = 0; ; attempt += 1) {
        options.signal.throwIfAborted();
        try {
          bytes = await options.read(start, end, options.signal);
          if (bytes.length !== end - start + 1) throw new Error('Short read');
          options.onRetry?.(null);
          break;
        } catch (err) {
          if (options.signal.aborted || !isRetryable(err)) throw err;
          options.onRetry?.(retryDelay(attempt));
          await wait(retryDelay(attempt), options.signal);
        }
      }
      for (let i = 0; i < count; i += 1) {
        // Checked before a byte of it is written (crypto-spec §7.5).
        await splitter.write(await session.openRecord(first + i, bytes.subarray(i * DROP_RECORD_BYTES, (i + 1) * DROP_RECORD_BYTES)));
      }
      options.onRecords(first + count);
    }
    return await splitter.finish();
  } catch (err) {
    await options.sink.abort();
    throw err;
  }
};
